import { ContentType } from '@/@types/common'
import type { Post } from '@/@types/post'
import { toAttachments } from '@/components/Attachment/utils'
import Avatar from '@/components/Avatar'
import type { LexicalEditorHandle } from '@/components/Editor/LexicalEditor'
import { CREATE_POST, EDIT_POST, EDIT_REPOST } from '@/constants/api'
import { cachePost, retrievePost } from '@/utils/fetchers/post'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { post } from '@/utils/request'
import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'
import { MIME_TYPES } from '@mantine/dropzone'
import clsx from 'clsx'
import { first, merge } from 'lodash-es'
import { motion } from 'motion/react'
import { useCallback, useMemo, useRef, type HTMLAttributes } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ZodError } from 'zod'
import AttachmentDropzone from '../../Attachment/AttachmentDropzone'
import AttachmentsPreview from '../../Attachment/AttachmentsPreview'
import useAttachmentsHelper from '../../Attachment/useAttachmentsHelper'
import AutoSaveService from '../AutoSaveService'
import ContentEditor from '../ContentEditor'
import RecoverDraftButton from '../RecoverDraftButton'
import PostAttachmentPicker from './PostAttachmentPicker'
import SubmitButton from './SubmitButton'
import TopicPicker from './TopicPicker'
import styles from './index.module.css'
import {
	CreatePostPayloadSchema,
	PostFormSchema,
	UpdatePostPayloadSchema,
	type PostFormValue,
} from './schemas'

export interface PostFormProps extends Omit<
	HTMLAttributes<HTMLFormElement>,
	'onSubmit' | 'defaultValue'
> {
	onSubmit?: (data: Post) => void
	defaultValue?: PostFormValue
}

export default function PostForm({ onSubmit, defaultValue, className, ...props }: PostFormProps) {
	const contentEditorRef = useRef<{ editor: LexicalEditorHandle }>(null)
	const user = useCurrentUser()

	const methods = useForm<PostFormValue>({
		defaultValues: useMemo(
			() =>
				merge(
					{},
					{
						attachment: {
							ids: [],
							map: {},
						},
						content: '',
					},
					defaultValue
				),
			[defaultValue]
		),
		mode: 'onChange',
		resolver: zodResolver(PostFormSchema),
	})
	const { isSubmitting, errors } = methods.formState

	const {
		handleAttachmentAdd,
		handleAttachmentDelete,
		handleAttachmentSwap,
		handleAttachmentUpdate,
		handleAttachmentsDropped,
		handlePasteAttachments,
		handleAttachmentRetry,
	} = useAttachmentsHelper(methods)

	const handleSubmit = useCallback(
		async (formValue: PostFormValue) => {
			const isCreateMode = !formValue.id
			let result: Post | null = null

			if (isCreateMode) {
				const payload = await CreatePostPayloadSchema.parseAsync({
					...formValue,
					syncToPersonalUpdate: true,
				}).catch((err) => {
					if (err instanceof ZodError) {
						const firstError = first(err.issues)?.message
						if (firstError) {
							toast.error(firstError)
						}
					}
					throw err
				})
				result = await post(CREATE_POST, payload).then((res) => res.data.data as Post)
			} else if (formValue.id) {
				// update
				const data = retrievePost(formValue.id)
				const payload = await UpdatePostPayloadSchema.parseAsync(formValue).catch((err) => {
					if (err instanceof ZodError) {
						const firstError = first(err.issues)?.message
						if (firstError) {
							toast.error(firstError)
						}
					}
					throw err
				})
				if (!payload || !data) {
					return
				}

				result = await post(data.type === ContentType.POST ? EDIT_POST : EDIT_REPOST, payload).then(
					(res) => res.data.data as Post
				)
			}

			if (!result) {
				throw new Error('Invalid form value')
			}

			methods.reset()
			contentEditorRef.current?.editor.clear()
			onSubmit?.(result)
			cachePost(result)
		},
		[methods, onSubmit]
	)

	return (
		<FormProvider {...methods}>
			<AttachmentDropzone
				onDrop={handleAttachmentsDropped}
				accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.mp4]}
			>
				<div className={styles.formContainer}>
					<Avatar data={user} className={styles.avatar} />
					<form
						onSubmit={methods.handleSubmit(handleSubmit)}
						className={clsx(styles.form, className)}
						{...props}
					>
						<Controller
							name="content"
							render={({ field }) => (
								<ContentEditor
									autoFocus
									{...field}
									ref={contentEditorRef}
									onPasteAttachments={handlePasteAttachments}
									onSubmit={methods.handleSubmit(handleSubmit)}
								/>
							)}
						/>

						<section className={styles.topicSection}>
							<Controller name="topic" render={({ field }) => <TopicPicker {...field} />} />
						</section>

						{/* attachment fields */}
						<footer className={styles.footer}>
							<Controller
								name="attachment"
								render={({ field }) => (
									<PostAttachmentPicker
										onAdd={handleAttachmentAdd}
										value={toAttachments(field.value)}
									/>
								)}
							/>

							<div className={styles.recoverButton}>
								<RecoverDraftButton
									draftType={ContentType.POST}
									onRecover={(nextData) => {
										const draft = nextData as PostFormValue
										for (const key in draft) {
											methods.setValue(
												key as keyof PostFormValue,
												draft[key as keyof PostFormValue],
												{
													shouldDirty: false,
												}
											)
										}
										contentEditorRef.current?.editor.setText(nextData.content)
									}}
								/>
							</div>

							<SubmitButton loading={isSubmitting} className={styles.submitButton} />
						</footer>

						<Controller
							name="attachment"
							render={({ field }) => (
								<AttachmentsPreview
									data={toAttachments(field.value)}
									onDelete={handleAttachmentDelete}
									onSwap={handleAttachmentSwap}
									onRetry={handleAttachmentRetry}
								/>
							)}
						/>

						<ErrorMessage
							name="attachment"
							errors={errors}
							render={({ message }) => (
								<motion.p
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									className={styles.errorMessage}
								>
									{message}
								</motion.p>
							)}
						/>
						<AutoSaveService type={ContentType.POST} />
					</form>
				</div>
			</AttachmentDropzone>
		</FormProvider>
	)
}
